import { useContext, useMemo } from 'react';
import { ConfiguratorContext } from '@/components/Configurator/ConfiguratorContext';
import { useDisplayContext, useTranslator } from '@canvas-logic/clp-shared-ui';
import { labelId } from '@canvas-logic/clp-localization';

/**
 * Translator for a `Mixed` / `ProductMerge` UI-config container's
 * title/description. ADR-0045 §"UI-config group localization":
 *
 * Canonicals are `cpt.{cptKey}.uicfg.group.{groupId}.{title|desc}` — groups
 * belong to a composite PT's UI tree, so the prefix is fixed to `cpt`.
 * `groupId` is the container's persisted `id` field (never its editable
 * `name`); the monotonic allocator on `IUICfgRootContainer` keeps that id
 * stable across delete-and-recreate edits, so translations bound to it do
 * not silently rebind.
 *
 * The hook is shaped so call sites can pass the live system value as the
 * fallback — legacy projects with no override still render the value the
 * author typed in the UI editor.
 */
type CompositeRootResolver = {
  getProduct?: () => { productType?: { productTypeKey?: string } } | undefined;
};

export function useUiCfgGroupLabel(
  groupId: string | undefined,
  kind: 'title' | 'desc',
  systemValue: string | undefined,
): string {
  const ctx = useContext(ConfiguratorContext);
  const api = ctx?.sdk?.api as CompositeRootResolver | undefined;
  const displayCtx = useDisplayContext();
  const rootCptKey = api?.getProduct?.()?.productType?.productTypeKey;
  // For uicfg.group resolution we want the *root* CPT, not a slot's filler
  // CPT — groups live on the root product's UI tree. `displayCtx.cptKey`
  // would resolve correctly only if the group is being rendered above any
  // composite slot subtree (the typical case).
  const activeCptKey = displayCtx.cptKey ?? rootCptKey;
  const translator = useTranslator();

  return useMemo(() => {
    const fallback = systemValue ?? '';
    if (!activeCptKey || !groupId) return fallback;
    const canonical =
      kind === 'title' ? labelId.uiCfgGroupTitle(activeCptKey, groupId) : labelId.uiCfgGroupDesc(activeCptKey, groupId);
    return translator.entity(canonical, displayCtx, fallback);
  }, [activeCptKey, groupId, kind, systemValue, translator, displayCtx]);
}
